import { useEffect, useState } from "react";
import { ArrowLeft, ChevronRight, Search } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ListSkeleton,
  OptionList,
  useFoodCategories,
  useInventoryItems,
  useSiteInventoryListId,
  type FoodCategory,
} from "@/components/waste/item-picker";

import { itemDisplayName } from "@/lib/inventory";
import { foodLabel } from "@/lib/food-labels";
import type { WasteEvent } from "@/hooks/use-waste-events";
import type { RelabelInput } from "@/hooks/use-waste-event-actions";
import { useTranslation } from "@/i18n/use-translation";

export type RelabelSelection = Omit<RelabelInput, "eventId">;

type Props = {
  event: WasteEvent | null;
  onClose: () => void;
  onSubmit: (selection: RelabelSelection) => void;
  isSubmitting: boolean;
};

/**
 * Spec §10.4/§10.5 "Something else" path — the category → sub-category → item
 * picker behind every relabel/correct action (Add label, Add detail, Not quite,
 * Change). Committing at the category or sub-category level is allowed; picking
 * a specific item is the "fillet steak moment" that jumps cost to the real
 * item price (§7.3). The relabel itself (confirm + recompute) is owned by the
 * page via `onSubmit`.
 */
export function RelabelPicker({ event, onClose, onSubmit, isSubmitting }: Props) {
  const { t } = useTranslation();
  const open = event !== null;
  const siteId = event?.site_id ?? null;

  const [category, setCategory] = useState<FoodCategory | null>(null);
  const [subcategory, setSubcategory] = useState<FoodCategory | null>(null);
  const [search, setSearch] = useState("");

  // Seed the drill from the event's known classification whenever a different
  // event opens the picker — staff land on the deepest unknown level and use
  // Back to change what the model already chose. When landing on the item
  // step, the AI's likely-item label pre-fills the search so a matching
  // inventory item is one tap (POS2-695 will make these adoptable outright).
  // Keyed by id only: a refetch swaps the object identity and must not reset
  // a drill in progress.
  useEffect(() => {
    setCategory(seedCategory(event));
    setSubcategory(seedSubcategory(event));
    setSearch((seedSubcategory(event) && event?.boosted_suggestion?.label) || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.id]);

  const categoriesQuery = useFoodCategories(open);
  const listQuery = useSiteInventoryListId(siteId, open);
  const listId = listQuery.data ?? null;
  const itemsQuery = useInventoryItems(listId, subcategory?.id ?? null);

  const allCategories = categoriesQuery.data ?? [];
  const topCategories = allCategories.filter((c) => !c.parent_id);
  const subcategories = category ? allCategories.filter((c) => c.parent_id === category.id) : [];

  const step: "category" | "subcategory" | "item" = !category
    ? "category"
    : !subcategory
      ? "subcategory"
      : "item";

  const norm = search.trim().toLowerCase();
  const matches = (label: string) => label.toLowerCase().includes(norm);

  function goBack() {
    setSearch("");
    if (step === "item") setSubcategory(null);
    else if (step === "subcategory") setCategory(null);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-sm font-medium text-gray-900">
            {t("waste.reclassify")}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t("waste.reclassifyDescription")}
          </DialogDescription>
        </DialogHeader>

        {/* Breadcrumb of the current drill position */}
        <div
          className="flex items-center gap-1.5 text-xs text-gray-500"
          data-testid="relabel-breadcrumb"
        >
          {step !== "category" && (
            <button
              type="button"
              onClick={goBack}
              className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              aria-label={t("waste.back")}
            >
              <ArrowLeft className="size-3.5" aria-hidden />
            </button>
          )}
          <span className={step === "category" ? "text-gray-900" : ""}>{t("waste.category")}</span>
          {category && (
            <>
              <ChevronRight className="size-3 text-gray-300" aria-hidden />
              <span className={step === "subcategory" ? "text-gray-900" : ""}>
                {foodLabel(t, category.name)}
              </span>
            </>
          )}
          {subcategory && (
            <>
              <ChevronRight className="size-3 text-gray-300" aria-hidden />
              <span className="text-gray-900">{foodLabel(t, subcategory.name)}</span>
            </>
          )}
        </div>

        {/* Commit at the current level without drilling all the way to an item */}
        {step === "subcategory" && category && (
          <Button
            variant="secondary"
            size="sm"
            className="h-8 justify-start text-xs"
            onClick={() =>
              onSubmit({ category_id: category.id, subcategory_id: null, inventory_item_id: null })
            }
            disabled={isSubmitting}
            data-testid="use-category"
          >
            {t("waste.useCategoryOnly", { name: foodLabel(t, category.name) })}
          </Button>
        )}
        {step === "item" && category && subcategory && (
          <Button
            variant="secondary"
            size="sm"
            className="h-8 justify-start text-xs"
            onClick={() =>
              onSubmit({
                category_id: category.id,
                subcategory_id: subcategory.id,
                inventory_item_id: null,
              })
            }
            disabled={isSubmitting}
            data-testid="use-subcategory"
          >
            {t("waste.useSubcategoryOnly", { name: foodLabel(t, subcategory.name) })}
          </Button>
        )}

        {/* Search filters the visible level */}
        <div className="relative">
          <Search
            className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-gray-300"
            aria-hidden
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("waste.searchStep", {
              what:
                step === "item"
                  ? t("waste.stepItems")
                  : step === "subcategory"
                    ? t("waste.stepSubcategories")
                    : t("waste.stepCategories"),
            })}
            className="pl-9 text-sm"
          />
        </div>

        <div className="max-h-72 overflow-y-auto" data-testid="relabel-options">
          {step === "category" &&
            (categoriesQuery.isLoading ? (
              <ListSkeleton />
            ) : (
              <OptionList
                options={topCategories.filter((c) => matches(foodLabel(t, c.name)))}
                empty={t("waste.noCategoriesMatch")}
                showChevron
                onSelect={(c) => {
                  setCategory(c);
                  setSearch("");
                }}
                render={(c) => foodLabel(t, c.name)}
              />
            ))}

          {step === "subcategory" && (
            <OptionList
              options={subcategories.filter((c) => matches(foodLabel(t, c.name)))}
              empty={t("waste.noSubcategories")}
              showChevron
              onSelect={(c) => {
                setSubcategory(c);
                setSearch("");
              }}
              render={(c) => foodLabel(t, c.name)}
            />
          )}

          {step === "item" &&
            (itemsQuery.isLoading ? (
              <ListSkeleton />
            ) : (
              <OptionList
                options={(itemsQuery.data ?? []).filter(
                  (i) => matches(foodLabel(t, itemDisplayName(i))) || matches(i.description),
                )}
                empty={t("waste.noItemsUseSubcategory")}
                showChevron={false}
                disabled={isSubmitting}
                onSelect={(i) =>
                  onSubmit({
                    category_id: category!.id,
                    subcategory_id: subcategory!.id,
                    inventory_item_id: i.id,
                  })
                }
                render={(i) => foodLabel(t, itemDisplayName(i))}
              />
            ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Only seed from real food_categories FKs (names come from the join then);
// AI-consensus labels without an id can't be submitted, so they don't seed.
function seedCategory(event: WasteEvent | null): FoodCategory | null {
  if (!event?.category_id) return null;
  return { id: event.category_id, name: event.category_name ?? "", parent_id: null, sort_order: 0 };
}

function seedSubcategory(event: WasteEvent | null): FoodCategory | null {
  if (!event?.category_id || !event.subcategory_id) return null;
  return {
    id: event.subcategory_id,
    name: event.subcategory_name ?? "",
    parent_id: event.category_id,
    sort_order: 0,
  };
}
